import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { INITIAL_JOB_POSTING_STEP } from '@/components/pages/job-posting/header/step-config';
import { PostingUnavailableDialog } from '@/components/pages/job-posting/posting-unavailable-dialog';
import {
  ChannelFormState,
  JobPostingForm,
  JobPostingStep,
  OpeningInfo,
  PostingResultData,
} from '@/components/pages/job-posting/types';
import { useJobPostingForm } from '@/components/pages/job-posting/use-job-posting-form';
import { buildChannelDefaults } from '@/components/pages/job-posting/utils/channel-defaults';
import { useLocalStorageState } from '@/hooks/use-local-storage-state';
import { useNavigationPaths } from '@/hooks/use-navigation-paths';
import { queryKeys } from '@/query-keys';
import { getChannelName } from '@/utils/channel-names';

import type { IneligibleOpening } from '@/components/pages/job-posting/types';
import type { BaseSyntheticEvent } from 'react';
import { useChannelDescription } from '@/components/pages/job-posting/select-channels/use-channel-description';

const SELECTED_OPENING_IDS_KEY = 'job-posting-selected-opening-ids';
const INELIGIBLE_OPENINGS_KEY = 'job-posting-ineligible-openings';
const OPENINGS_INFO_KEY = 'job-posting-openings-info';

/**
 * Shape every caller of `openJobPostingForm` passes per opening.
 *
 * Only `id` is required. `title` is stored in the openings map so downstream
 * UI (the openings panel, etc.) doesn't need to wait on the channels API.
 * `active` and `isSourcingEnabled` default to `true`;
 */
interface OpeningSelection {
  id: string;
  title?: string;
  active?: boolean;
  isSourcingEnabled?: boolean;
}

type OpenJobPostingFormProps = {
  openings: OpeningSelection[];
  channelSlugs?: string[];
};
export interface JobPostingContextType {
  /**
   * Set of steps that have been completed.
   */
  completedSteps: Set<JobPostingStep>;
  markStepCompleted: (step: JobPostingStep) => void;
  /**
   * Distinct **real** channel slugs that are currently selected (derived from
   * `ChannelFormState.slug` for every entry in `channels`). Use this for membership
   * checks like `.includes(ZIPRECRUITER)` and for presenting the user with "which
   * channels did I pick?".
   *
   * Pool is included here as `'pool'` when the user has added the Pool channel.
   * For Vonq, multiple products of the same channel collapse into a single slug.
   */
  selectedChannelSlugs: string[];
  /**
   * Form-state keys currently in `channels` — `Object.keys(channels)`. Each Vonq
   * product is its own entry. Pool is keyed as `'pool'`. Use this when you need an
   * entry count (e.g. "is this the last channel form entry?").
   */
  selectedChannelKeys: string[];
  selectedOpeningIds: string[];
  setSelectedOpeningIds: Dispatch<SetStateAction<string[]>>;
  /**
   * Snapshot of `selectedOpeningIds` captured when the form was opened. Stays
   * constant across step changes and as openings are removed during the flow,
   * so consumers (e.g. the channels query) can fetch once on form open without
   * re-firing every time the user removes an opening or switches steps.
   */
  initialSelectedOpeningIds: string[];
  currentStep: JobPostingStep;
  setCurrentStep: Dispatch<SetStateAction<JobPostingStep>>;
  hookForm: UseFormReturn<JobPostingForm>;
  clearJobPostingStorage: () => void;
  totalBudget: number;
  handlePostJob: (e?: BaseSyntheticEvent) => Promise<void>;
  isSubmitting: boolean;
  poolOpeningsWithConflicts: string[] | undefined;
  setPoolOpeningsWithConflicts: Dispatch<SetStateAction<string[] | undefined>>;
  // List of openings that are inactive or sourcing-disabled
  ineligibleOpenings: IneligibleOpening[];
  /**
   * Map of `externalId → OpeningInfo` for every eligible opening currently in
   * the job-posting flow. Seeded by `openJobPostingForm` from each
   * `OpeningSelection` the caller provided. When the caller doesn't pass a
   * `title`, the entry is `{ externalId }` only and `useOpening` backfills the
   * title from the channels API.
   *
   * Prefer reading via the `useOpening` hook — it merges this stored metadata
   * with derived data (selected channels, opening-level budget, …) — instead
   * of consuming the map directly.
   */
  openings: Record<string, OpeningInfo>;
  openJobPostingForm: (props: OpenJobPostingFormProps) => void;
  postingResult: PostingResultData | null;
  setPostingResult: Dispatch<SetStateAction<PostingResultData | null>>;
  dismissResult: () => void;
}

export const JobPostingContext = createContext<
  JobPostingContextType | undefined
>(undefined);

export function JobPostingProvider({ children }: PropsWithChildren) {
  const navigate = useNavigate();
  const { postJobPath } = useNavigationPaths();
  const queryClient = useQueryClient();
  const { getTitle } = useChannelDescription();

  const [selectedOpeningIds, setSelectedOpeningIds] = useLocalStorageState<
    string[]
  >(SELECTED_OPENING_IDS_KEY, []);
  const [ineligibleOpenings, setIneligibleOpenings] = useLocalStorageState<
    IneligibleOpening[]
  >(INELIGIBLE_OPENINGS_KEY, []);
  const [openings, setOpenings] = useLocalStorageState<
    Record<string, OpeningInfo>
  >(OPENINGS_INFO_KEY, {});
  const [initialSelectedOpeningIds, setInitialSelectedOpeningIds] =
    useState<string[]>(selectedOpeningIds);

  const [currentStep, setCurrentStep] = useState<JobPostingStep>(
    INITIAL_JOB_POSTING_STEP,
  );
  const [poolOpeningsWithConflicts, setPoolOpeningsWithConflicts] =
    useState<string[]>();
  const [isPostingUnavailableDialogOpen, setIsPostingUnavailableDialogOpen] =
    useState(false);
  const [postingResult, setPostingResult] = useState<PostingResultData | null>(
    null,
  );
  const hookFormRef = useRef<UseFormReturn<JobPostingForm> | null>(null);

  const [completedSteps, setCompletedSteps] = useState<Set<JobPostingStep>>(
    new Set(),
  );
  const markStepCompleted = useCallback((step: JobPostingStep) => {
    setCompletedSteps(prev => {
      if (prev.has(step)) return prev;
      return new Set(prev).add(step);
    });
  }, []);

  /**
   * Clears the job posting storage and resets the state.
   * To be used when the user navigates away from the job posting form.
   */
  const clearJobPostingStorage = useCallback(() => {
    localStorage.removeItem(SELECTED_OPENING_IDS_KEY);
    localStorage.removeItem(INELIGIBLE_OPENINGS_KEY);
    localStorage.removeItem(OPENINGS_INFO_KEY);
    setSelectedOpeningIds([]);
    setInitialSelectedOpeningIds([]);
    setIneligibleOpenings([]);
    setOpenings({});
    setCurrentStep(INITIAL_JOB_POSTING_STEP);
    setCompletedSteps(new Set());
    setPoolOpeningsWithConflicts(undefined);
    setPostingResult(null);
    queryClient.removeQueries({ queryKey: queryKeys.jobPosting.poolConflicts });
    hookFormRef.current?.reset({});
  }, [queryClient, setSelectedOpeningIds, setIneligibleOpenings, setOpenings]);

  const { hookForm, handlePostJob, isSubmitting } = useJobPostingForm({
    setPostingResult,
    setCurrentStep,
  });
  hookFormRef.current = hookForm;

  const dismissResult = useCallback(() => {
    clearJobPostingStorage();
    const dashboardPath = window.location.pathname.replace(
      '/post-job',
      '/dashboard',
    );
    navigate(dashboardPath);
  }, [navigate, clearJobPostingStorage]);

  /**
   * Opens the job posting form. If the openings are ineligible, the alert dialog will be opened.
   *
   * When `channelSlugs` is provided, the channels are pre-populated on the form state
   *
   * A single pass is used to split into eligible/ineligible and seed the openings map so downstream UI has titles without an API roundtrip.
   */
  const openJobPostingForm = useCallback(
    ({ openings: requested, channelSlugs }: OpenJobPostingFormProps) => {
      setCurrentStep(INITIAL_JOB_POSTING_STEP);
      setCompletedSteps(new Set());
      setPostingResult(null);
      setPoolOpeningsWithConflicts(undefined);
      hookFormRef.current?.reset({});

      const ineligible: IneligibleOpening[] = [];
      const nextOpenings: Record<string, OpeningInfo> = {};
      const eligibleIds: string[] = [];

      for (const opening of requested) {
        const isInactive = opening.active === false;
        const isSourcingDisabled =
          !isInactive && opening.isSourcingEnabled === false;

        if (isInactive || isSourcingDisabled) {
          ineligible.push({
            externalId: opening.id,
            title: opening.title ?? opening.id,
            reason: isInactive ? 'inactive' : 'sourcing_disabled',
          });
          continue;
        }

        eligibleIds.push(opening.id);
        nextOpenings[opening.id] = {
          externalId: opening.id,
          ...(opening.title ? { title: opening.title } : {}),
        };
      }

      setIneligibleOpenings(ineligible);
      setOpenings(nextOpenings);

      // Block navigation if every requested opening is ineligible — show the alert dialog and let the caller's page stay where it is.
      if (eligibleIds.length === 0 && ineligible.length > 0) {
        setIsPostingUnavailableDialogOpen(true);
        return;
      }

      setSelectedOpeningIds(eligibleIds);
      setInitialSelectedOpeningIds(eligibleIds);

      // Pre-populate the form when the caller pre-selected channels for the user.
      if (channelSlugs && channelSlugs.length > 0 && hookFormRef.current) {
        const channels = channelSlugs.reduce<Record<string, ChannelFormState>>(
          (acc, slug) => ({
            ...acc,
            [slug]: buildChannelDefaults({
              channelSlug: slug,
              channelName: getChannelName(slug),
              displayTitle: getTitle(slug, getChannelName(slug)),
              openingIds: eligibleIds,
              unsupportedOpenings: [],
            }),
          }),
          {},
        );

        hookForm.setValue('channels', channels);
      }

      navigate(postJobPath);
    },
    [
      getTitle,
      hookForm,
      navigate,
      postJobPath,
      setIneligibleOpenings,
      setOpenings,
      setSelectedOpeningIds,
    ],
  );

  const channels = useWatch({ control: hookForm.control, name: 'channels' });
  // Form-state keys: one entry per channel form slice. Pool is keyed as 'pool'.
  const selectedChannelKeys = useMemo(
    () => Object.keys(channels ?? {}),
    [channels],
  );
  // Distinct real channel slugs. Collapses multiple Vonq products into a single slug
  // (e.g. two Monster products → ['monster']). Pool appears as 'pool' when selected.
  const selectedChannelSlugs = useMemo(() => {
    const slugs: string[] = [];
    for (const channel of Object.values(channels ?? {})) {
      if (!slugs.includes(channel.slug)) slugs.push(channel.slug);
    }
    return slugs;
  }, [channels]);

  const totalBudget = !channels
    ? 0
    : Object.values(channels).reduce(
        (acc, channel) => acc + (channel.totalChannelBudget ?? 0),
        0,
      );

  /* Handles the change of the posting unavailable dialog. If the dialog is closed, the state will be reset. */
  const handlePostingUnavailableDialogChange = (open: boolean) => {
    if (!open) {
      setIsPostingUnavailableDialogOpen(false);
      setIneligibleOpenings([]);
    }
  };

  const contextValue = useMemo<JobPostingContextType>(
    () => ({
      completedSteps,
      markStepCompleted,
      selectedChannelSlugs,
      selectedChannelKeys,
      selectedOpeningIds,
      setSelectedOpeningIds,
      initialSelectedOpeningIds,
      currentStep,
      setCurrentStep,
      hookForm,
      totalBudget,
      clearJobPostingStorage,
      handlePostJob,
      isSubmitting,
      poolOpeningsWithConflicts,
      setPoolOpeningsWithConflicts,
      ineligibleOpenings,
      openings,
      openJobPostingForm,
      postingResult,
      setPostingResult,
      dismissResult,
    }),
    [
      completedSteps,
      markStepCompleted,
      selectedChannelSlugs,
      selectedChannelKeys,
      selectedOpeningIds,
      setSelectedOpeningIds,
      initialSelectedOpeningIds,
      currentStep,
      setCurrentStep,
      hookForm,
      totalBudget,
      clearJobPostingStorage,
      handlePostJob,
      isSubmitting,
      poolOpeningsWithConflicts,
      setPoolOpeningsWithConflicts,
      ineligibleOpenings,
      openings,
      openJobPostingForm,
      postingResult,
      setPostingResult,
      dismissResult,
    ],
  );

  return (
    <JobPostingContext.Provider value={contextValue}>
      {children}
      <PostingUnavailableDialog
        open={isPostingUnavailableDialogOpen}
        onOpenChange={handlePostingUnavailableDialogChange}
        ineligibleOpenings={ineligibleOpenings}
      />
    </JobPostingContext.Provider>
  );
}

export function useJobPostingContext(): JobPostingContextType {
  const context = useContext(JobPostingContext);

  if (!context) {
    throw new Error(
      'useJobPostingContext must be used within a JobPostingProvider',
    );
  }

  return context;
}
